import { authStorage, type StoredTokens } from "./auth-storage";

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
  skipAuthRefresh?: boolean;
  /**
   * Throw a 403 `CANONICAL_HOST_REQUIRED` to the caller instead of redirecting
   * to the login page. Only for public pages that render their own wrong-host
   * screen (the signature links); logins rely on the redirect.
   */
  skipCanonicalRedirect?: boolean;
}

const envApiBase = import.meta.env.VITE_API_URL as string | undefined;
export const API_BASE_URL = envApiBase?.trim() || "/api";

export class ApiError extends Error {
  code?: string;
  data?: unknown;
  status?: number;
  isAuthError?: boolean;
  /** Body `retryAfterSeconds`, else the `Retry-After` header in whole seconds. */
  retryAfterSeconds?: number;
}

export class AuthRefreshUnavailableError extends Error {
  cause?: unknown;

  constructor(message = "Auth refresh unavailable", cause?: unknown) {
    super(message);
    this.name = "AuthRefreshUnavailableError";
    this.cause = cause;
  }
}

export const isAuthRefreshUnavailableError = (
  error: unknown,
): error is AuthRefreshUnavailableError =>
  error instanceof AuthRefreshUnavailableError ||
  (error instanceof Error && error.name === "AuthRefreshUnavailableError");

let refreshInFlight: Promise<StoredTokens | null> | null = null;

const resolveCanonicalHost = (data: unknown) => {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const candidate = (data as { canonicalHost?: unknown }).canonicalHost;
  return typeof candidate === "string" && candidate.trim().length > 0
    ? candidate
    : null;
};

export const resolveLoginPath = (pathname?: string) => {
  const currentPath =
    pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname);
  if (currentPath.startsWith("/admin")) return "/admin/login";
  if (currentPath.startsWith("/participant/portal")) {
    return "/participant/portal/login";
  }
  if (currentPath.startsWith("/participant")) return "/participant/login";
  return "/login";
};

export const sanitizeLoginReturnTarget = (candidate?: string | null) => {
  if (typeof candidate !== "string") {
    return null;
  }

  const normalized = candidate.trim();
  if (!normalized.startsWith("/")) {
    return null;
  }
  if (normalized.startsWith("//")) {
    return null;
  }
  // Reject a backslash, tab, LF or CR anywhere in the target. The URL parser
  // reads a backslash after the leading slash, and drops tab and newline
  // characters, so such a target can become `//host` and leave the app origin.
  if (/[\\\t\n\r]/.test(normalized)) {
    return null;
  }

  return normalized;
};

export const readLoginReturnTarget = (params: {
  state?: unknown;
  search?: string;
}) => {
  const fromValue =
    typeof params.state === "object" && params.state !== null
      ? (params.state as { from?: unknown }).from
      : undefined;
  const pathname =
    typeof fromValue === "object" && fromValue !== null
      ? (fromValue as { pathname?: unknown }).pathname
      : undefined;
  const search =
    typeof fromValue === "object" && fromValue !== null
      ? (fromValue as { search?: unknown }).search
      : undefined;

  const stateTarget =
    typeof pathname === "string"
      ? sanitizeLoginReturnTarget(
          `${pathname}${typeof search === "string" ? search : ""}`,
        )
      : null;
  if (stateTarget) {
    return stateTarget;
  }

  const searchParams = new URLSearchParams(params.search ?? "");
  return sanitizeLoginReturnTarget(searchParams.get("from"));
};

export const buildCanonicalLocation = (canonicalHost: string, path: string) => {
  if (typeof window === "undefined") {
    return path;
  }
  return `${window.location.protocol}//${canonicalHost}${path}`;
};

export const buildLoginRedirectTarget = (params: {
  loginPath: string;
  canonicalHost?: string | null;
  currentHost?: string | null;
  currentPathWithSearch?: string | null;
}) => {
  const loginPath = sanitizeLoginReturnTarget(params.loginPath) ?? params.loginPath;
  const preserveFrom =
    !params.canonicalHost ||
    !params.currentHost ||
    params.canonicalHost === params.currentHost;
  const currentTarget = preserveFrom
    ? sanitizeLoginReturnTarget(params.currentPathWithSearch)
    : null;
  const from =
    currentTarget &&
    currentTarget !== loginPath &&
    !currentTarget.startsWith(`${loginPath}?`)
      ? currentTarget
      : null;
  const loginTarget = from
    ? `${loginPath}?from=${encodeURIComponent(from)}`
    : loginPath;

  return params.canonicalHost
    ? buildCanonicalLocation(params.canonicalHost, loginTarget)
    : loginTarget;
};

export const redirectToLogin = (options?: {
  canonicalHost?: string | null;
  loginPath?: string;
}) => {
  authStorage.clear();
  if (typeof window === "undefined") return;

  const loginPath = options?.loginPath ?? resolveLoginPath();
  const target = buildLoginRedirectTarget({
    canonicalHost: options?.canonicalHost,
    loginPath,
    currentHost: window.location.host,
    currentPathWithSearch: `${window.location.pathname}${window.location.search}`,
  });

  const currentLocation = `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}`;
  if (currentLocation !== target) {
    window.location.replace(target);
  }
};

export const logoutAndRedirect = (canonicalHost?: string | null) => {
  redirectToLogin({ canonicalHost });
};

const handleCanonicalHostRequired = (error: ApiError, loginPath?: string) => {
  const canonicalHost = resolveCanonicalHost(error.data);
  redirectToLogin({
    canonicalHost,
    loginPath,
  });
};

export async function refreshTokens(
  refreshToken?: string | null,
): Promise<StoredTokens | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: refreshToken
          ? {
              "Content-Type": "application/json",
            }
          : undefined,
        body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
      });
    } catch (error) {
      throw new AuthRefreshUnavailableError(
        error instanceof Error ? error.message : "Auth refresh unavailable",
        error,
      );
    }

    if (!response.ok) {
      const apiError = await buildApiError(response);
      if (
        response.status === 403 &&
        apiError.code === "CANONICAL_HOST_REQUIRED"
      ) {
        handleCanonicalHostRequired(apiError);
      } else {
        authStorage.clear();
      }
      return null;
    }

    const data = (await response.json()) as { tokens: StoredTokens };
    authStorage.save(data.tokens);
    return data.tokens;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

const buildHeaders = (options: ApiRequestOptions) => {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const hasBody = options.body !== undefined && options.body !== null;
  const hasContentTypeHeader = Object.keys(headers).some(
    (key) => key.toLowerCase() === "content-type",
  );
  if (hasBody && !isFormData && !hasContentTypeHeader) {
    headers["Content-Type"] = "application/json";
  }

  if (!options.skipAuth) {
    const tokens = authStorage.get();
    if (tokens?.accessToken) {
      headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
  }
  return headers;
};

// `Retry-After` may also be an HTTP date; only whole seconds are used.
const parseRetryAfterHeader = (value: string | null) => {
  const trimmed = value?.trim() ?? "";
  return /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : undefined;
};

const buildApiError = async (response: Response) => {
  const apiError = new ApiError("Unbekannter Fehler");
  apiError.status = response.status;
  apiError.retryAfterSeconds = parseRetryAfterHeader(
    response.headers.get("Retry-After"),
  );
  try {
    const raw = await response.text();
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as {
          message?: string;
          code?: string;
          errorCode?: string;
          data?: unknown;
          canonicalHost?: unknown;
          retryAfterSeconds?: unknown;
        };
        apiError.message = parsed.message ?? apiError.message;
        apiError.code = parsed.errorCode ?? parsed.code;
        apiError.data = parsed.data ?? parsed;
        if (
          typeof parsed.retryAfterSeconds === "number" &&
          Number.isFinite(parsed.retryAfterSeconds)
        ) {
          apiError.retryAfterSeconds = parsed.retryAfterSeconds;
        }
      } catch (_err) {
        apiError.message = raw;
      }
    }
  } catch (_err) {
    // ignore parse errors
  }
  return apiError;
};

export const isUnauthorizedError = (error: unknown) => {
  if (error instanceof ApiError) {
    return error.status === 401 && error.isAuthError === true;
  }
  return false;
};

export async function apiRequestRaw(
  path: string,
  options: ApiRequestOptions = {},
  retry = true,
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const headers = buildHeaders(options);
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers,
  });

  if (
    response.status === 401 &&
    !options.skipAuth &&
    !options.skipAuthRefresh &&
    retry
  ) {
    const refreshed = await refreshTokens();
    if (refreshed?.accessToken) {
      return apiRequestRaw(path, options, false);
    }
  }

  if (!response.ok) {
    const apiError = await buildApiError(response);
    if (
      response.status === 401 &&
      !options.skipAuth &&
      !options.skipAuthRefresh
    ) {
      apiError.isAuthError = true;
      logoutAndRedirect();
    }
    if (
      response.status === 403 &&
      apiError.code === "CANONICAL_HOST_REQUIRED" &&
      !options.skipCanonicalRedirect
    ) {
      handleCanonicalHostRequired(apiError);
    }
    throw apiError;
  }

  return response;
}

export async function apiRequestBlob(
  path: string,
  options: ApiRequestOptions = {},
  retry = true,
): Promise<Blob> {
  const response = await apiRequestRaw(path, options, retry);
  return response.blob();
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
  retry = true,
): Promise<T> {
  const response = await apiRequestRaw(path, options, retry);
  if (response.status === 204) {
    return undefined as T;
  }

  const raw = await response.text();
  if (!raw) {
    return undefined as T;
  }

  try {
    return JSON.parse(raw) as T;
  } catch (_err) {
    return raw as unknown as T;
  }
}
